import type { ForwardRef } from '@excalidraw/excalidraw/types/utility-types';
import clsx from 'clsx';
import * as React from 'react';

import type { InlineIconData } from '@gitbook/doc-core';
import { Icon, type IconName } from '@gitbook/icons';
import type { Inline } from '@gitbook/slate';
import type { InlineProps } from '@gitbook/slate-react';
import {
    AtomButton,
    type AtomButtonProps,
    IconPicker,
    Paper,
    Separator,
    Tooltip,
    usePopoverClose,
} from '@gitbook/spine-react';

import {
    ContentEditorColorContext,
    DEFAULT_EDITOR_COLOR_GROUPS,
    useContentEditorColor,
} from '../colors/ContentEditorColorContext';
import { VoidInlineWithPopover } from '../common';
import type { ColorPickerOption } from '../common/colorPickerOptions';

/**
 * Renderer for an icon inline node.
 */
export function InlineIcon(props: InlineProps): React.ReactElement {
    const { node } = props;

    return (
        <VoidInlineWithPopover
            {...props}
            renderSpan={() => <InlineIconSpan node={node} />}
            popover={<InlineIconPopover {...props} />}
        />
    );
}

function InlineIconPopover(props: InlineProps): React.ReactElement {
    const { node, editor } = props;

    const [query, setQuery] = React.useState('');
    const closePopover = usePopoverClose();

    const data = node.data.toFrozenObject() as InlineIconData;
    const color = useContentEditorColor({ type: 'text', color: data.color ?? null });

    return (
        <Paper className="flex flex-col">
            <IconPicker
                header={
                    <InlineIconColorPicker
                        color={color}
                        onChange={(color) => {
                            editor.change((change) => {
                                change.setDataByKey(node.key, ['color'], color?.key ?? undefined);
                            });
                        }}
                    />
                }
                color={color?.color ?? undefined}
                onPick={(icon) => {
                    editor.change((change) => {
                        change.setDataByKey(node.key, ['icon'], icon);
                    });
                    closePopover();
                }}
                query={query}
                onChangeQuery={setQuery}
            />
        </Paper>
    );
}

function InlineIconColorPicker(props: {
    color: ColorPickerOption | null;
    onChange: (color: ColorPickerOption | null) => void;
}) {
    const { color: currentColor, onChange } = props;
    const colorContext = React.useContext(ContentEditorColorContext);
    const colors = colorContext.groups.find((group) => group.key === 'text')?.colors;
    if (!colors || colors.length === 0) {
        return null;
    }
    return (
        <div className="flex flex-wrap gap-2 px-2 pt-3 pb-1">
            {colors.map((color, index) => {
                const previous = index > 0 ? colors[index - 1] : null;
                const hasSeparator =
                    previous &&
                    !checkIsDefaultColor(previous.key) &&
                    checkIsDefaultColor(color.key);
                const isSelected = currentColor ? currentColor.color === color.color : false;
                return (
                    <React.Fragment key={color.key}>
                        {hasSeparator && <Separator orientation="vertical" />}
                        <Tooltip label={color.title}>
                            <ColorSwatch
                                key={color.key}
                                color={color.color}
                                isSelected={isSelected}
                                onPress={() => {
                                    onChange(isSelected ? null : color);
                                }}
                            />
                        </Tooltip>
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/**
 * Check if a color is part of the default colors (not custom ones).
 */
function checkIsDefaultColor(color: string) {
    return DEFAULT_EDITOR_COLOR_GROUPS.some((group) => group.colors.some((c) => c.key === color));
}

const ColorSwatch = React.forwardRef(function ColorSwatch(
    props: {
        color: string;
        isSelected: boolean;
    } & Omit<AtomButtonProps, 'style'>,
    ref: ForwardRef<HTMLButtonElement>
): React.ReactElement {
    const { color, isSelected = false, className, ...rest } = props;
    return (
        <AtomButton
            ref={ref}
            data-selected={isSelected ? '' : undefined}
            className={clsx(
                'relative size-6 rounded-full',
                'outline-none forced-color-adjust-none',
                'data-[focus-visible]:shadow-focus-ring',
                'data-[selected]:after:absolute data-[selected]:after:inset-0 data-[selected]:after:content-[""]',
                'data-[selected]:after:border data-[selected]:after:border-black data-[selected]:after:border-solid',
                'data-[selected]:after:-outline-offset-2 data-[selected]:after:rounded-full data-[selected]:after:outline data-[selected]:after:outline-1 data-[selected]:after:outline-white',
                className
            )}
            style={{ backgroundColor: color }}
            {...rest}
        />
    );
});

function InlineIconSpan(props: { node: Inline }): React.ReactElement {
    const { node } = props;

    const data = node.data.toFrozenObject() as InlineIconData;
    const editorColor = useContentEditorColor({ type: 'text', color: data.color ?? null });

    return (
        <Icon
            icon={data.icon as IconName}
            className="inline-block size-[1em] align-middle"
            style={{ backgroundColor: editorColor?.color }}
        />
    );
}
